import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { signInRequest } from '@/store/modules/auth/actions'
import { pushNotification } from '@/store/modules/notification/actions'
import errorHandler from '@/utils/errorHandler'
import useService from 'main/hooks/useService'
import { getLoginTheme } from 'services/authServices'
import { Logo, StyledBox, StyledPaper } from './styles'
import { Notification, Spinner } from 'presentation/components'
import { Button, Typography } from '@mui/material'
import { faEnvelope, faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { faLock } from '@fortawesome/free-solid-svg-icons'
import { Controller, useForm } from 'react-hook-form'
import { validateEmail } from 'validations-br'
import { Link } from 'presentation/components/atoms/link'
import { TextField } from 'presentation/components/molecules/textField/textField'
import { Icon } from 'presentation/components/atoms/Icon'
import { getResaleSiteFromUrl } from '@/utils/functionUtils'
import { useAtom } from 'jotai'
import { globalThemeAtom } from 'config/providers/global/globalTheme'
import _ from 'lodash'
import { Movidesk } from 'presentation/components/movidesk'
import { signOut } from 'store/modules/auth/actions'

const Login = () => {
  const dispatch = useDispatch()
  const queryParameters = new URLSearchParams(window.location.search)
  const isKeycloak = queryParameters.get('isKeycloak')

  const auth = useSelector((state) => state.auth)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const [theme, setTheme] = useAtom(globalThemeAtom)

  const [fetchCustomInterfaceState, fetchCustomInterfaceRequest] = useService(getLoginTheme, {
    onCompleted: (res) => {
      setTheme(_.merge(theme, JSON.parse(res.data?.resale?.theme)))
    },
  })
  const resaleData = fetchCustomInterfaceState.response?.data?.resale

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      site: getResaleSiteFromUrl(),
    },
  })

  useEffect(() => {
    dispatch(signOut())
    fetchCustomInterfaceRequest(getResaleSiteFromUrl())
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (isDirty && !auth.loading) {
      dispatch(pushNotification(errorHandler(auth.failure)))
    }
    // eslint-disable-next-line
  }, [auth, dispatch])

  const onSubmit = (data) => {
    dispatch(signInRequest({ ...data, isKeycloak }))
  }

  const handleImageError = (e) => (e.target.style.display = 'none')
  return (
    <StyledBox>
      <Movidesk />
      <Notification />
      <StyledPaper>
        {auth.loading ? (
          <Spinner.Box>
            <Spinner />
          </Spinner.Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Logo
              src={resaleData?.images?.login}
              onError={handleImageError}
              sx={{ display: resaleData?.images?.login ? 'initial' : 'none' }}
              alt='Logo'
            />
            <Typography variant='h1'>Bem-vindo!</Typography>
            <Typography variant='string'>
              Para acessar seu portal, insira seu e-mail e senha cadastrados na plataforma.
            </Typography>
            <Controller
              control={control}
              name='email'
              rules={{
                required: 'Campo requerido',
                validate: {
                  isEmailInValid: (value) => validateEmail(value) || 'Esse não é um e-mail valido',
                },
              }}
              render={({ field: { value, onChange }, fieldState: { error, isDirty } }) => (
                <TextField
                  label='E-mail'
                  placeholder='exemplo@mail.com'
                  startAdornment={<Icon icon={faEnvelope} />}
                  value={value}
                  helperText={error?.message}
                  onChange={onChange}
                  error={error}
                  success={!error && isDirty}
                />
              )}
            />
            <Controller
              control={control}
              name='password'
              rules={{
                required: 'Campo requerido',
              }}
              render={({ field: { value, onChange }, fieldState: { error, isDirty } }) => (
                <TextField
                  label='Senha'
                  placeholder='Informe a senha'
                  startAdornment={<Icon icon={faLock} />}
                  endAdornment={
                    isPasswordVisible ? (
                      <Icon icon={faEyeSlash} onClick={() => setIsPasswordVisible(false)} />
                    ) : (
                      <Icon icon={faEye} onClick={() => setIsPasswordVisible(true)} />
                    )
                  }
                  value={value}
                  helperText={error?.message}
                  onChange={onChange}
                  error={error}
                  success={!error && isDirty}
                  type={isPasswordVisible ? 'text' : 'password'}
                />
              )}
            />
            <Link to={'/esqueci_minha_senha'}>Esqueci minha senha</Link>
            <Button
              sx={{
                height: '60px',
                width: '100%',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: '700',
                marginTop: '82px',
                backgroundColor: resaleData?.theme?.primary?.main,
                '&:hover': {
                  backgroundColor: resaleData?.theme?.primary?.dark,
                },
              }}
              variant='contained'
              type='submit'
            >
              Fazer Login
            </Button>
          </form>
        )}
      </StyledPaper>
    </StyledBox>
  )
}

export default Login
